import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button, Spinner, Form} from 'react-bootstrap'
import {getEconomyByIso3, createEconomy, updateEconomy} from './core/_requests'
import {getAllEconomyGroups} from '../economy-groups/core/_requests'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useNavigate, useParams, useLocation} from 'react-router-dom'
import {Economy} from './core/_models'
import {EconomyGroup} from '../economy-groups/core/_models'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import Select from 'react-select'
import {showSwal} from '../../../../ui-kit'

const confirmDialog = (title: string) => {
  return showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })
}

const EconomiesDetailPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const [internalLoading, setInternalLoading] = useState(false)
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {iso3} = useParams()
  const isDetailMode = Boolean(iso3)
  const isAddMode = !iso3
  const [isEditMode, setIsEditMode] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [groups, setGroups] = useState<EconomyGroup[]>([])

  const [detail, setDetail] = useState<Economy>({
    iso3: '',
    iso2: '',
    name_en: '',
    name_fr: '',
    comtrade: '',
    trains_id: null,
    gta_id: null,
    eping_id: '',
    tf_survey: 0,
    group_ids: [],
  })

  const fetchDetail = async () => {
    if (!iso3) return
    try {
      setInternalLoading(true)
      const res = await getEconomyByIso3(axiosPrivate, iso3)
      if (res.data && res.data.data) {
        setDetail(res.data.data)
      }
    } catch (error) {
      console.error(error)
      showSwal('Error', 'Failed to fetch details', 'error')
    } finally {
      setInternalLoading(false)
    }
  }

  const fetchGroups = async () => {
    try {
      const res = await getAllEconomyGroups(axiosPrivate)
      if (res.data && res.data.data) {
        setGroups(res.data.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchGroups()
    if (isDetailMode) {
      fetchDetail()
    }
  }, [iso3])

  const location = useLocation()
  useEffect(() => {
    if (location.pathname.endsWith('/edit')) {
      setIsEditMode(true)
    } else {
      setIsEditMode(false)
    }
  }, [location.pathname, iso3])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setDetail((prev) => ({
      ...prev,
      [name]: name === 'iso3' || name === 'iso2' ? value.toUpperCase() : value,
    }))
  }

  useEffect(() => {
    const isValid =
      detail.iso3.trim().length === 3 &&
      detail.iso2.trim().length === 2 &&
      detail.name_en.trim() !== '' &&
      detail.comtrade.trim() !== '' &&
      detail.tf_survey != 0
    setIsFormValid(isValid)
  }, [detail])

  const handleSubmit = () => {
    confirmDialog(
      `Are you sure you want to ${isEditMode ? 'update' : 'create'} this economy?`
    ).then((result) => {
      if (!result.isConfirmed) return

      setInternalLoading(true)
      const payload = {
        iso3: detail.iso3,
        iso2: detail.iso2,
        name_en: detail.name_en,
        name_fr: detail.name_fr,
        comtrade: detail.comtrade,
        trains_id: detail.trains_id,
        gta_id: detail.gta_id,
        eping_id: detail.eping_id,
        tf_survey: detail.tf_survey,
        group_ids: detail.group_ids,
      }

      const action = isEditMode
        ? updateEconomy(axiosPrivate, iso3, payload)
        : createEconomy(axiosPrivate, payload)

      action
        .then((res) => {
          if (res.status === 200) {
            showSwal({
              title: `Data ${isEditMode ? 'updated' : 'created'}`,
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              if (isEditMode) {
                window.location.reload()
              } else {
                navigate('/economies')
              }
            })
          }
        })
        .catch((err) => {
          console.error(err)
          const errorMsg =
            err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} data`
          showSwal('Error', errorMsg, 'error')
        })
        .finally(() => {
          setInternalLoading(false)
        })
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/economies')
        } else {
          setIsEditMode(false)
        }
      }
    })
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
          {title: 'Economies', path: 'economies', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {isAddMode ? 'Add Economy' : isEditMode ? 'Edit Economy' : detail.name_en || 'Detail'}
      </PageTitle>
      <div>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-globe px-2'></span>
                      <h3 className='text-gray-700'>
                        {isAddMode
                          ? 'Add New Economy'
                          : isEditMode
                          ? `Edit ${detail.name_en}`
                          : `Detail ${detail.name_en}`}
                      </h3>
                    </div>
                    <div className='d-flex gap-2'>
                      {ability.can('update', subject) && isDetailMode && !isEditMode ? (
                        <Button
                          className='btn btn-primary d-flex gap-4 align-items-center'
                          onClick={() => setIsEditMode(true)}
                          disabled={internalLoading}
                        >
                          <span className='fa fa-pencil'></span>
                          Edit
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!internalLoading ? (
                  <>
                    {isAddMode || isEditMode ? (
                      <Form>
                        <Row>
                          <h3 className='mb-5'>General Information</h3>
                          <Col md={6} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='required text-primary me-0 ps-0'>
                                ISO3
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='iso3'
                                value={detail.iso3}
                                onChange={handleChange}
                                placeholder='e.g., KHM'
                                maxLength={3}
                                disabled={isEditMode}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='required text-primary me-0 ps-0'>
                                ISO2
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='iso2'
                                value={detail.iso2}
                                onChange={handleChange}
                                placeholder='e.g., KH'
                                maxLength={2}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='required text-primary me-0 ps-0'>
                                Name (EN)
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='name_en'
                                value={detail.name_en}
                                onChange={handleChange}
                                placeholder='Enter name in English'
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='text-primary me-0 ps-0'>Name (FR)</Form.Label>
                              <Form.Control
                                type='text'
                                name='name_fr'
                                value={detail.name_fr || ''}
                                onChange={handleChange}
                                placeholder='Enter name in French'
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='required text-primary me-0 ps-0'>
                                Comtrade
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='comtrade'
                                value={detail.comtrade}
                                onChange={handleChange}
                                placeholder='Enter Comtrade code'
                                maxLength={5}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='text-primary me-0 ps-0'>
                                Group Memberships
                              </Form.Label>
                              <Select
                                styles={{
                                  valueContainer: (baseStyles) => ({
                                    ...baseStyles,
                                    height: '3.27rem',
                                    overflowY: 'auto',
                                  }),
                                }}
                                isMulti
                                name='group_ids'
                                placeholder='Select group(s)'
                                options={groups.map((group) => ({
                                  value: group.id,
                                  label: group.name,
                                }))}
                                value={
                                  detail.group_ids?.map((id: number) => {
                                    const g = groups.find((group) => group.id === id)
                                    return {
                                      value: id,
                                      label: g ? g.name : `Group ${id}`,
                                    }
                                  }) || []
                                }
                                onChange={(selected) => {
                                  setDetail((prev) => ({
                                    ...prev,
                                    group_ids: selected ? selected.map((s) => s.value) : [],
                                  }))
                                }}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <hr />
                        <Row>
                          <h3 className='mb-5'>IDs & Survey</h3>
                          <Col md={4} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='text-primary me-0 ps-0'>TRAINS ID</Form.Label>
                              <Form.Control
                                type='number'
                                min={0}
                                name='trains_id'
                                value={detail.trains_id || ''}
                                onChange={handleChange}
                                placeholder='Enter TRAINS ID'
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='text-primary me-0 ps-0'>GTA ID</Form.Label>
                              <Form.Control
                                type='number'
                                min={0}
                                name='gta_id'
                                value={detail.gta_id || ''}
                                onChange={handleChange}
                                placeholder='Enter GTA ID'
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='text-primary me-0 ps-0'>ePing ID</Form.Label>
                              <Form.Control
                                type='text'
                                name='eping_id'
                                value={detail.eping_id || ''}
                                onChange={handleChange}
                                placeholder='Enter ePing ID'
                                maxLength={5}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group>
                          <Form.Label className='required text-primary me-0 ps-0'>
                            TF Survey (%)
                          </Form.Label>
                          <Form.Control
                            type='number'
                            min={0}
                            name='tf_survey'
                            value={detail.tf_survey}
                            onChange={handleChange}
                            max={100}
                          />
                        </Form.Group>

                        <div className='row mt-10'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={!isFormValid}
                            >
                              Submit
                            </Button>
                          </div>
                        </div>
                      </Form>
                    ) : (
                      <div className='detail-agreement'>
                        <h3 className='mb-5'>General Information</h3>
                        <div className='row mb-4'>
                          <div className='col-md-6'>
                            <Form.Label>ISO3</Form.Label>
                            <div className='data-value'>{detail.iso3}</div>
                          </div>
                          <div className='col-md-6'>
                            <Form.Label>ISO2</Form.Label>
                            <div className='data-value'>{detail.iso2}</div>
                          </div>
                          <div className='col-md-6 mt-3'>
                            <Form.Label>Name (EN)</Form.Label>
                            <div className='data-value'>{detail.name_en}</div>
                          </div>
                          <div className='col-md-6 mt-3'>
                            <Form.Label>Name (FR)</Form.Label>
                            <div className='data-value'>{detail.name_fr || '-'}</div>
                          </div>
                          <div className='col-md-6 mt-3'>
                            <Form.Label>Comtrade</Form.Label>
                            <div className='data-value'>{detail.comtrade}</div>
                          </div>
                        </div>

                        <hr />
                        <h3 className='mb-5'>IDs & Survey</h3>
                        <div className='row mb-4'>
                          <div className='col-md-4'>
                            <Form.Label>TRAINS ID</Form.Label>
                            <div className='data-value'>{detail.trains_id || '-'}</div>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label>GTA ID</Form.Label>
                            <div className='data-value'>{detail.gta_id || '-'}</div>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label>ePing ID</Form.Label>
                            <div className='data-value'>{detail.eping_id || '-'}</div>
                          </div>
                          <div className='col-md-12 mt-3'>
                            <Form.Label>TF Survey</Form.Label>
                            <div className='data-value'>{detail.tf_survey}%</div>
                          </div>
                        </div>

                        <hr />
                        <h3 className='mb-5'>Groups</h3>
                        <div className='row'>
                          <div className='col-md-12'>
                            <Form.Label>Assigned Groups</Form.Label>
                            <div className='data-value'>
                              {detail.group_ids
                                .map((id) => groups.find((g) => g.id === id)?.name)
                                .join(', ') || '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '200px'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default EconomiesDetailPage
